import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { Filter } from 'lucide-react';
import { toast } from 'sonner';

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All Products' },
    { value: 'sportswear', label: 'Sportswear' },
    { value: 'streetwear', label: 'Streetwear' },
    { value: 'comfort-wear', label: 'Comfort Wear' },
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter((p) => p.category === selectedCategory));
    }
  }, [selectedCategory, products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/products');
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="shop-page">
      {/* Hero */}
      <section className="section-spacing bg-brand-timberwolf" data-testid="shop-hero">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-brand-pearl leading-none mb-6" data-testid="shop-hero-heading">
              Premium Apparel Collection
            </h1>
            <p className="text-base sm:text-lg text-brand-walnut max-w-3xl mx-auto leading-relaxed">
              Ready-to-wear sportswear, streetwear, and comfort wear designed for performance and style.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters & Products */}
      <section className="section-spacing" data-testid="shop-products">
        <div className="container-custom">
          {/* Filter Bar */}
          <div className="flex items-center justify-between mb-12 pb-6 border-b border-brand-pearl/10">
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-brand-walnut" />
              <span className="text-sm font-medium text-brand-walnut">Filter by:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  data-testid={`filter-${cat.value}`}
                  className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                    selectedCategory === cat.value
                      ? 'bg-brand-pearl text-brand-ivory'
                      : 'bg-brand-timberwolf text-brand-walnut hover:bg-brand-pearl/10'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="text-center py-20" data-testid="shop-loading">
              <div className="w-12 h-12 border-4 border-brand-pearl/20 border-t-brand-pearl rounded-full animate-spin mx-auto mb-4" />
              <p className="text-brand-walnut">Loading products...</p>
            </div>
          )}

          {/* Products Grid */}
          {!loading && filteredProducts.length === 0 && (
            <div className="text-center py-20" data-testid="shop-empty">
              <p className="text-lg text-brand-walnut">No products found. Check back soon!</p>
            </div>
          )}

          {!loading && filteredProducts.length > 0 && (
            <div className="product-grid">
              {filteredProducts.map((product, idx) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  className="card-hover"
                  data-testid={`product-card-${idx}`}
                >
                  <Link to={`/product/${product.slug}`} className="block">
                    <div className="bg-brand-ivory rounded-sm border border-brand-pearl/10 overflow-hidden">
                      <div className="parallax-img">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-72 object-cover"
                        />
                      </div>
                      <div className="p-6">
                        <div className="mb-2">
                          <span className="text-xs font-mono uppercase tracking-widest text-brand-walnut">
                            {product.category}
                          </span>
                        </div>
                        <h3 className="text-xl font-heading font-semibold text-brand-pearl mb-2">
                          {product.name}
                        </h3>
                        <p className="text-2xl font-bold text-brand-pearl">
                          ₹{product.price.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ShopPage;